/* eslint-disable @typescript-eslint/ban-ts-comment */
import * as React from "react";

import { Box, Grid, makeStyles, Typography } from "@material-ui/core";
import { v4 as uuidv4 } from "uuid";

import { MopInfoTooltip } from "client/components/MopInfoTooltip/MopInfoTooltip";
import { FieldMaps, useMerchantModel, useTranslation } from "client/hooks";
import { useNormalizedBrandPaletteValues } from "client/hooks/useNormalizedBrandThemeValues";
import { logAmplitudeEvent } from "client/lib/amplitude";
import {
  AllMerchantFields,
  CountryList,
  Field,
  FieldSet,
} from "client/lib/apt-merchant";
import { PhoneField as PhoneFieldType } from "client/lib/apt-merchant/src/fields/storeDetails/PhoneField";

import {
  FileUploadField,
  InputField,
  MultiSelectField,
  PhoneField,
  PlacesField,
  RadioInputField,
  SelectField,
} from "./fields";

const useStyles = makeStyles((theme) => {
  const { accordionDetailsSubtitleFg } = useNormalizedBrandPaletteValues(theme);

  return {
    fieldHeader: {
      fontSize: "0.9rem",
      lineHeight: "1.4rem",
      color: accordionDetailsSubtitleFg,
    },
  };
});

interface FieldsToRender {
  fieldSet: FieldSet<any>;
  uiFieldMap?: FieldMaps;
  currentConversationalPage?: number;
}

const FieldHeader: React.FC<{
  stringFieldHeader?: string;
  customFieldHeader?: React.ReactElement;
}> = ({ stringFieldHeader, customFieldHeader }) => {
  const classes = useStyles();
  if (Boolean(stringFieldHeader)) {
    return (
      <Box mb={1}>
        <Typography className={classes.fieldHeader} variant="body2">
          {stringFieldHeader}
        </Typography>
      </Box>
    );
  }

  return customFieldHeader ? customFieldHeader : null;
};

const processFields = ({
  fields,
  processorCb,
  uiFieldMap,
}: {
  fields: Field<any>[];
  processorCb?: (fields: Field<any>[]) => Field<any>[];
  uiFieldMap?: FieldMaps;
}): Field<any>[] => {
  const fieldsToShow = fields
    .filter((field) => field.shown)
    .sort(
      (a, b) =>
        // @ts-ignore
        (uiFieldMap?.[a.propertyName]?.order ?? 0) -
        // @ts-ignore
        (uiFieldMap?.[b.propertyName]?.order ?? 0)
    );
  return processorCb ? processorCb(fieldsToShow) : fieldsToShow;
};

const showUkBankAccountTooltip = (
  registeredCountry: CountryList | undefined
): boolean =>
  registeredCountry ? registeredCountry == "United Kingdom" : false;

export const FieldRenderer: React.FC<FieldsToRender> = ({
  fieldSet,
  uiFieldMap,
  currentConversationalPage,
}) => {
  const { getFieldSetUpdater, merchant } = useMerchantModel();
  const t = useTranslation();

  const updateField = getFieldSetUpdater(fieldSet);
  const showFields = processFields({
    fields: fieldSet.fields,
    uiFieldMap,
    processorCb: (_fields) => {
      let fieldsToShow = _fields;

      if (currentConversationalPage) {
        // if this is a conversational page (currently only on lead page), filter by fields that are meant to be on this page
        fieldsToShow = fieldsToShow.filter((f) => {
          const fieldConfig =
            // @ts-ignore
            uiFieldMap?.[f.propertyName as keyof AllMerchantFields];
          return fieldConfig?.pageNumber
            ? fieldConfig?.pageNumber === currentConversationalPage
            : true;
        });
      }
      return fieldsToShow;
    },
  });

  const getHelperText = (field: Field<any>) => {
    // helper text is either a.) error message string b.) string from resource file or c.) custom node passed into uiFieldMap. We check for truthyness in that order

    const errorMessage = // @ts-ignore
      uiFieldMap?.[field.propertyName as keyof AllMerchantFields]?.error;
    if (errorMessage) {
      logAmplitudeEvent(
        "FIELD_ERROR",
        `${fieldSet.fieldSetName} - ${field.propertyName} - ${errorMessage}`
      );
      return errorMessage;
    }

    const nodeHelperText = // @ts-ignore
      uiFieldMap?.[field.propertyName as keyof AllMerchantFields]
        ?.customHelperText;
    if (nodeHelperText) {
      return nodeHelperText;
    }

    const fieldHelperText = field.i18nValues.helperText;
    if (fieldHelperText) {
      return fieldHelperText;
    }
  };

  const businessType = merchant.companyInformation.businessType.value;
  const registeredCountry = merchant.companyInformation.registeredCountry.value;

  const ukBankAccountTooltipBodyText =
    businessType === "Sole Trader"
      ? t("UkBankAccountNameTooltipBodySoleTrader")
      : t("UkBankAccountNameTooltipBody");

  return (
    <Grid container spacing={2}>
      {showFields.map((field) => (
        <Grid
          key={field.propertyName}
          xs={12}
          lg={
            // @ts-ignore
            uiFieldMap?.[field.propertyName as keyof AllMerchantFields]
              ?.width ?? 6
          }
          item
        >
          <>
            <FieldHeader
              stringFieldHeader={field.i18nValues.fieldHeader}
              customFieldHeader={
                // @ts-ignore
                uiFieldMap?.[field.propertyName as keyof AllMerchantFields]
                  ?.customFieldHeader
              }
            />
            {field.uiFieldType === "Date" && (
              <Box position="relative">
                <InputField
                  field={field}
                  handleChange={updateField}
                  hasError={Boolean(
                    // @ts-ignore
                    uiFieldMap?.[field.propertyName as keyof AllMerchantFields]
                      ?.error
                  )}
                  helperText={getHelperText(field)}
                  inputMask={
                    // @ts-ignore
                    uiFieldMap?.[field.propertyName as keyof AllMerchantFields]
                      ?.inputMask
                  }
                />
              </Box>
            )}
            {field.uiFieldType === "Input" ? (
              <Box position="relative">
                <InputField
                  field={field}
                  handleChange={updateField}
                  hasError={Boolean(
                    // @ts-ignore
                    uiFieldMap?.[field.propertyName as keyof AllMerchantFields]
                      ?.error
                  )}
                  helperText={getHelperText(field)}
                  inputMask={
                    // @ts-ignore
                    uiFieldMap?.[field.propertyName as keyof AllMerchantFields]
                      ?.inputMask
                  }
                />
                {field.propertyName === "stockMarketLister" && (
                  <MopInfoTooltip
                    bodyText={t("uiStockMarketListerTooltipBody")}
                  />
                )}
                {field.propertyName === "ssn" && (
                  <MopInfoTooltip
                    headerText={t("uiSsnTooltipHeader")}
                    bodyText={t("uiSsnTooltipBody")}
                  />
                )}
                {field.propertyName === "bankAccountName" && (
                  <MopInfoTooltip
                    bodyText={ukBankAccountTooltipBodyText}
                    showTooltip={showUkBankAccountTooltip(registeredCountry)}
                  />
                )}
              </Box>
            ) : null}
            {field.uiFieldType === "Phone" && (
              <Box position="relative">
                <PhoneField
                  field={field as PhoneFieldType}
                  handleChange={updateField}
                  hasError={Boolean(
                    // @ts-ignore
                    uiFieldMap?.[field.propertyName as keyof AllMerchantFields]
                      ?.error
                  )}
                  helperText={getHelperText(field)}
                />
              </Box>
            )}
            {field.uiFieldType === "Select" && (
              <Box position="relative">
                <SelectField
                  errorMessage={
                    // @ts-ignore
                    uiFieldMap?.[field.propertyName as keyof AllMerchantFields]
                      ?.error
                  }
                  field={field}
                  handleChange={updateField}
                  hasError={Boolean(
                    // @ts-ignore
                    uiFieldMap?.[field.propertyName as keyof AllMerchantFields]
                      ?.error
                  )}
                />
                {field.propertyName === "acquirer" && (
                  <MopInfoTooltip
                    headerText={t("uiAcquirerTooltipHeader")}
                    bodyText={t("uiAcquirerTooltipBody")}
                  />
                )}
              </Box>
            )}
            {field.uiFieldType === "MultiSelect" && (
              <MultiSelectField
                errorMessage={
                  // @ts-ignore
                  uiFieldMap?.[field.propertyName as keyof AllMerchantFields]
                    ?.error
                }
                field={field}
                handleChange={updateField}
                hasError={Boolean(
                  // @ts-ignore
                  uiFieldMap?.[field.propertyName as keyof AllMerchantFields]
                    ?.error
                )}
              />
            )}
            {field.uiFieldType === "FileUpload" && (
              <FileUploadField
                fileExtensionsToAccept={field.uploadFileExtensions}
                propertyName={field.propertyName}
                customHelperText={
                  // @ts-ignore
                  uiFieldMap?.[field.propertyName as keyof AllMerchantFields]
                    ?.customHelperText
                }
                errorMessage={
                  // @ts-ignore
                  uiFieldMap?.[field.propertyName as keyof AllMerchantFields]
                    ?.error
                }
                filename={field.uploadFilename}
                hasError={Boolean(
                  // @ts-ignore
                  uiFieldMap?.[field.propertyName as keyof AllMerchantFields]
                    ?.error
                )}
                helperText={field.i18nValues?.helperText}
                id={`${field.propertyName}${uuidv4()}`}
                label={field.i18nValues.label}
                updateFieldValue={(value) => {
                  updateField(field, value);
                }}
              />
            )}
            {field.uiFieldType === "RadioInput" && (
              <RadioInputField
                errorMessage={
                  // @ts-ignore
                  uiFieldMap?.[field.propertyName as keyof AllMerchantFields]
                    ?.error
                }
                field={field}
                handleChange={(value) => {
                  updateField(field, value);
                }}
              />
            )}
            {field.uiFieldType === "LocationAutocomplete" ? (
              <PlacesField
                error={
                  // @ts-ignore
                  uiFieldMap?.[field.propertyName as keyof AllMerchantFields]
                    ?.error
                }
                field={field}
                fieldSet={fieldSet}
                helperText={getHelperText(field)}
                updateFieldModel={(value) => {
                  updateField(field, value);
                }}
              />
            ) : null}
          </>
        </Grid>
      ))}
    </Grid>
  );
};
